import { Delegate, animate, timeline } from "vendor";
/* ─── accordion-disclosure ──────────────────────────────────────────── */
/* ─── custom-details ────────────────────────────────────────────────── */
var CustomDetails = class extends HTMLElement {
  #onSummaryClickedListener;
  constructor() {
    super();
    this.#onSummaryClickedListener = this.#onSummaryClicked.bind(this);
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.toggle(true, !event.detail.load));
      this.addEventListener("shopify:block:deselect", (event) => this.toggle(false, !event.detail.load));
    }
  }
  static get observedAttributes() {
    return ["open", "aria-expanded"];
  }
  connectedCallback() {
    this.disclosureElement.setAttribute("aria-expanded", this.disclosureElement.open ? "true" : "false");
    this.summaryElement.addEventListener("click", this.#onSummaryClickedListener);
  }
  disconnectedCallback() {
    this.summaryElement.removeEventListener("click", this.#onSummaryClickedListener);
  }
  get disclosureElement() {
    return this.querySelector("details");
  }
  get summaryElement() {
    return this.disclosureElement.firstElementChild;
  }
  get contentElement() {
    return this.disclosureElement.lastElementChild;
  }
  toggle(force = void 0, animate26 = true) {
    const newValue = typeof force === "boolean" ? force : !(this.disclosureElement.getAttribute("aria-expanded") === "true");
    if (newValue) {
      this.open({ instant: !animate26 });
    } else {
      this.close();
    }
  }
  async open({ instant = false } = {}) {
    if (this.disclosureElement.getAttribute("aria-expanded") === "true") {
      return;
    }
    this.disclosureElement.open = true;
    this.disclosureElement.setAttribute("aria-expanded", "true");
    const controls = this.createShowAnimationControls();
    if (instant) {
      controls.finish();
    }
  }
  async close() {
    this.disclosureElement.setAttribute("aria-expanded", "false");
    if (!this.disclosureElement.open) {
      return;
    }
    this.createHideAnimationControls()?.finished.then((event) => {
      if (event !== void 0) {
        this.disclosureElement.removeAttribute("open");
      }
    });
  }
  createShowAnimationControls() {
  }
  createHideAnimationControls() {
  }
  #onSummaryClicked(event) {
    event.preventDefault();
    if (this.disclosureElement.open && this.summaryElement.hasAttribute("data-follow-link")) {
      return window.location.href = this.summaryElement.getAttribute("data-follow-link");
    }
    this.toggle();
  }
};
/* ─── accordion-disclosure ──────────────────────────────────────────── */
var AccordionDisclosure = class extends CustomDetails {
  createShowAnimationControls() {
    this.disclosureElement.style.overflow = "hidden";
    const animationControls = timeline([
      [this.disclosureElement, { height: [`${this.summaryElement.clientHeight}px`, `${this.disclosureElement.scrollHeight}px`] }, { duration: 0.25, easing: "ease" }],
      [this.contentElement, { opacity: [0, 1], transform: ["translateY(4px)", `translateY(0)`] }, { duration: 0.15, at: "-0.1" }]
    ]);
    animationControls.finished.then(() => {
      this.disclosureElement.style.height = null;
      this.disclosureElement.style.overflow = null;
    });
    return animationControls;
  }
  createHideAnimationControls() {
    const animationControls = timeline([
      [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
      [this.disclosureElement, { height: [`${this.disclosureElement.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: "<", easing: "ease" }]
    ]);
    animationControls.finished.then(() => {
      this.disclosureElement.style.height = null;
    });
    return animationControls;
  }
};
if (!window.customElements.get("accordion-disclosure")) {
  window.customElements.define("accordion-disclosure", AccordionDisclosure);
}
/* ─── menu-disclosure ───────────────────────────────────────────────── */
var _MenuDisclosure = class _MenuDisclosure2 extends CustomDetails {
  #detectClickOutsideListener;
  #detectEscKeyboardListener;
  #detectFocusOutListener;
  #detectHoverListener;
  #detectHoverOutsideListener;
  #hoverTimer;
  constructor() {
    super();
    this.#detectClickOutsideListener = this.#detectClickOutside.bind(this);
    this.#detectEscKeyboardListener = this.#detectEscKeyboard.bind(this);
    this.#detectFocusOutListener = this.#detectFocusOut.bind(this);
    this.#detectHoverOutsideListener = this.#detectHoverOutside.bind(this);
    this.#detectHoverListener = this.#detectHover.bind(this);
    this.disclosureElement.addEventListener("mouseover", this.#detectHoverListener.bind(this));
    this.disclosureElement.addEventListener("mouseout", this.#detectHoverListener.bind(this));
    this.disclosureElement.addEventListener("keydown", this.#handleKeyboard.bind(this));
  }
  get trigger() {
    return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
  }
  get mouseOverDelayTolerance() {
    return 250;
  }
  async open({ instant = false } = {}) {
    super.open({ instant });
    document.addEventListener("click", this.#detectClickOutsideListener);
    document.addEventListener("keydown", this.#detectEscKeyboardListener);
    document.addEventListener("focusout", this.#detectFocusOutListener);
    document.addEventListener("mouseover", this.#detectHoverOutsideListener);
  }
  async close() {
    super.close();
    document.removeEventListener("click", this.#detectClickOutsideListener);
    document.removeEventListener("keydown", this.#detectEscKeyboardListener);
    document.removeEventListener("focusout", this.#detectFocusOutListener);
    document.removeEventListener("mouseover", this.#detectHoverOutsideListener);
  }
  #handleKeyboard(event) {
    if (event.key === "Escape") {
      this.close();
      this.summaryElement.focus();
    }
  }
  #detectClickOutside(event) {
    if (this.trigger !== "click") {
      return;
    }
    if (!this.contains(event.target) && !(event.target.closest("details") instanceof _MenuDisclosure)) {
      this.toggle(false);
    }
  }
  #detectHover(event) {
    if (this.trigger !== "hover") {
      return;
    }
    if (event.type === "mouseover") {
      clearTimeout(this.#hoverTimer);
      this.toggle(true);
    } else if (event.type === "mouseout") {
      this.#hoverTimer = setTimeout(() => this.toggle(false), this.mouseOverDelayTolerance);
    }
  }
  #detectHoverOutside(event) {
    if (this.trigger !== "hover") {
      return;
    }
    const closestDetails = event.target.closest("details");
    if (closestDetails instanceof _MenuDisclosure && closestDetails !== this && !closestDetails.contains(this) && !this.contains(closestDetails)) {
      clearTimeout(this.#hoverTimer);
      this.toggle(false);
    }
  }
  #detectEscKeyboard(event) {
    if (event.code === "Escape") {
      const targetMenu = event.target.closest("details[open]");
      if (targetMenu && targetMenu instanceof _MenuDisclosure) {
        targetMenu.toggle(false);
        event.stopPropagation();
      }
    }
  }
  #detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.toggle(false);
    }
  }
};
var MenuDisclosure = _MenuDisclosure;
/* ─── tabs ──────────────────────────────────────────────────────────── */
var Tabs = class extends HTMLElement {
  #buttons;
  #componentID;
  #delegate;
  #panels;
  constructor() {
    super();
    this.#componentID = crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4);
    this.#buttons = [];
    this.#panels = [];
    this.#delegate = new Delegate(this);
    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(this.querySelector("template").content.cloneNode(true));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.selectedIndex = this.#buttons.indexOf(event.target));
    }
    this.#delegate.on("click", 'button[role="tab"]', this.#onButtonClicked.bind(this));
    this.shadowRoot.addEventListener("slotchange", this.#onSlotChange.bind(this));
    this.addEventListener("keydown", this.#handleKeyboard);
  }
  static get observedAttributes() {
    return ["selected-index"];
  }
  connectedCallback() {
    this.#setupComponent();
    this.selectedIndex = this.selectedIndex;
  }
  disconnectedCallback() {
    this.#delegate.destroy();
  }
  get animationDuration() {
    return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.3;
  }
  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }
  set selectedIndex(index) {
    this.setAttribute("selected-index", Math.min(Math.max(index, 0), Math.max(0, this.#buttons.length - 1)).toString());
    this.style.setProperty("--selected-index", this.selectedIndex.toString());
  }
  attributeChangedCallback(name, oldValue, newValue) {
    this.#buttons.forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));
    if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
      this.transition(this.#panels[parseInt(oldValue)], this.#panels[parseInt(newValue)]);
    }
  }
  async transition(fromPanel, toPanel) {
    const beforeHeight = this.clientHeight;
    await animate(fromPanel, { transform: ["translateY(0px)", "translateY(10px)"], opacity: [1, 0] }, { duration: this.animationDuration }).finished;
    fromPanel.hidden = true;
    toPanel.hidden = false;
    await timeline([
      [this, { height: [`${beforeHeight}px`, `${this.clientHeight}px`], overflow: ["hidden", "visible"] }, { duration: 0.15, easing: [0.85, 0, 0.15, 1] }],
      [toPanel, { transform: ["translateY(10px)", "translateY(0px)"], opacity: [0, 1] }, { duration: this.animationDuration, at: "+0.1" }]
    ]).finished;
    this.style.removeProperty("height");
  }
  #setupComponent() {
    this.#buttons = Array.from(this.shadowRoot.querySelector('slot[name="title"]')?.assignedNodes() ?? [], (item) => item.matches("button") && item || item.querySelector("button"));
    this.#panels = Array.from(this.shadowRoot.querySelector('slot[name="content"]')?.assignedNodes() ?? []);
    this.#buttons.forEach((button, index) => {
      button.setAttribute("role", "tab");
      button.setAttribute("aria-controls", `tab-panel-${this.#componentID}-${index}`);
      button.id = `tab-${this.#componentID}-${index}`;
    });
    this.#panels.forEach((panel, index) => {
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("aria-labelledby", `tab-${this.#componentID}-${index}`);
      panel.id = `tab-panel-${this.#componentID}-${index}`;
      panel.hidden = index !== this.selectedIndex;
    });
    this.style.setProperty("--item-count", this.#buttons.length.toString());
  }
  #onButtonClicked(event, button) {
    this.selectedIndex = this.#buttons.indexOf(button);
  }
  #onSlotChange() {
    this.#setupComponent();
  }
  #handleKeyboard(event) {
    const index = this.#buttons.indexOf(document.activeElement);
    if (index === -1 || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    if (event.key === "ArrowLeft") {
      this.selectedIndex = (this.selectedIndex - 1 + this.#buttons.length) % this.#buttons.length;
    } else {
      this.selectedIndex = (this.selectedIndex + 1 + this.#buttons.length) % this.#buttons.length;
    }
    this.#buttons[this.selectedIndex].focus();
  }
};
if (!window.customElements.get("x-tabs")) {
  window.customElements.define("x-tabs", Tabs);
}
export {
  CustomDetails,
  MenuDisclosure
};
